import { ReactComponent as CloseIcon } from "assets/icons/close.svg";
import { ReactComponent as MailOpenIcon } from "assets/icons/mail-open.svg";
import { ReactComponent as StarFilledIcon } from "assets/icons/star-filled.svg";
import { ReactComponent as StarIcon } from "assets/icons/star.svg";
import { ReactComponent as Delete } from "assets/icons/trash.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import { getDropdownValue } from "helpers/DropdownHelper";
import {
    DEFAULT_PAGE_SIZE_OPTION,
    handlePageClick,
    handlePageSizeChange,
} from "helpers/PaginationHelper";
import {
    BoolArrayFilter,
    Button,
    Checkbox,
    ConfirmationModal,
    CustomTable,
    Filter,
    NotificationSource,
    NotificationSourceTranslation,
    OptionType,
    Pagination,
    TableColumn,
    TableType,
    useOutsideClick,
    useTitle,
} from "modules/common";
import { useSignalRHub } from "modules/common/hooks/useSignalRHub";
import { handleKeyDown } from "modules/common/utils/keyboardUtils";
import {
    useDeleteUserNotifications,
    useGetUserNotifications,
    useHighlightUserNotification,
    useMarkNotificationsRead,
    UserNotificationDetails,
    UserNotificationsFilters,
} from "modules/communication";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Row } from "react-table";
import { toast } from "react-toastify";
import { AppRoutes, RouteParams } from "routing";
import { UserNotificationView } from "services";

const toastStyle: CSSProperties = {
    color: "black",
    background: getComputedStyle(document.documentElement).getPropertyValue("--color-background"),
};

const progressStyle: CSSProperties = {
    background: getComputedStyle(document.documentElement).getPropertyValue(
        "--color-secondary-dark-gray"
    ),
};

export const UserNotifications = () => {
    useTitle("Moje poruke - eSavjetnik");

    const { search } = useLocation();
    const history = useHistory();

    const filtersRef = useRef<{ clearFilters: () => void }>(null);

    const [selectedUserNotificationIds, setSelectedUserNotificationIds] = useState<string[]>([]);
    const [selectedUserNotificationId, setSelectedUserNotificationId] = useState<string | null>(
        null
    );

    // opens user notification by ID
    useEffect(() => {
        const params = new URLSearchParams(search);
        if (params.get(RouteParams.NotificationId))
            setSelectedUserNotificationId(params.get(RouteParams.NotificationId));
        else setSelectedUserNotificationId(null);
    }, [search]);

    // modal state to confirm user action
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

    // filters
    const [searchFilter, setSearchFilter] = useState("");
    const [isHighlightedFilter, setIsHighlightedFilter] = useState(false);
    const [sourceFilter, setSourceFilter] = useState<NotificationSource[]>([]);
    const [isReadFilter, setIsReadFilter] = useState<BoolArrayFilter[]>([]);
    const [from, setFrom] = useState<Date | null>(null);
    const [to, setTo] = useState<Date | null>(null);

    const [startDate, setStartDate] = useState<Date | null | undefined>(null);
    const [endDate, setEndDate] = useState<Date | null | undefined>(null);

    // page size
    const [pageSize, setPageSize] = useState<OptionType>(DEFAULT_PAGE_SIZE_OPTION);
    const [currentPage, setCurrentPage] = useState(1);

    // dropdown states
    const [showActionDropdown, setShowActionDropdown] = useState(false);
    const actionDropdownRef = useRef<HTMLDivElement>(null);
    useOutsideClick(actionDropdownRef, () => setShowActionDropdown(false));
    const handleActionDropdownClick = () => setShowActionDropdown(prev => !prev);

    // signalR hub for receiving alarm notifications in real time
    const { listen } = useSignalRHub("alarms");

    // action to invoke on message
    listen("AlarmNotificationCreated", _ => {
        toast.info(
            <div className="f-r g-12 f-ai-c">
                <span>Aktivirani su novi alarmi.</span>
                <Button
                    onClick={() => filtersRef.current?.clearFilters()}
                    size="small"
                    variant="primary"
                    style={{ whiteSpace: "nowrap" }}
                >
                    Osvježite poruke
                </Button>
            </div>,
            {
                position: "bottom-right",
                style: toastStyle,
                progressStyle: progressStyle,
                // onClick not needed because toasts close on click
                closeButton: () => <CloseIcon width={12} height={12} strokeWidth={3} />,
            }
        );
    });

    const { data: userNotifications, isFetching: isLoading } = useGetUserNotifications({
        search: searchFilter,
        isHighlighted: isHighlightedFilter,
        source: sourceFilter,
        isRead: isReadFilter,
        from: from ?? undefined,
        to: to ?? undefined,
        page: currentPage,
        pageSize: parseInt(pageSize.value),
    });

    // mutation methods
    const highlightUserNotification = useHighlightUserNotification();
    const deleteUserNotifications = useDeleteUserNotifications();
    const markNotificationsRead = useMarkNotificationsRead();

    // unselect notifications on filter change
    useEffect(() => {
        setSelectedUserNotificationIds([]);
    }, [
        searchFilter,
        isHighlightedFilter,
        sourceFilter,
        isReadFilter,
        from,
        to,
        currentPage,
        pageSize,
    ]);

    // methods for manipulating selected user notifications
    const addRemoveAllUserNotifications = useMemo(
        () => (event: React.ChangeEvent<HTMLInputElement>) => {
            const allUserNotifications =
                userNotifications?.userNotificationsList.map<string>(i => i.id) ?? [];

            if (event.target.checked) {
                const merged = [
                    ...selectedUserNotificationIds,
                    ...allUserNotifications.filter(
                        id => !selectedUserNotificationIds.some(existingId => existingId === id)
                    ),
                ];
                setSelectedUserNotificationIds(merged);
                return;
            }

            const filteredOut = selectedUserNotificationIds.filter(
                existingId => !allUserNotifications.some(id => id === existingId)
            );
            setSelectedUserNotificationIds(filteredOut);
        },
        [selectedUserNotificationIds, userNotifications?.userNotificationsList]
    );

    const addRemoveUserNotification = useMemo(
        () => (id: string) => {
            const selectedUserNotification = userNotifications?.userNotificationsList.find(
                i => i.id === id
            );
            if (!selectedUserNotification) return;

            setSelectedUserNotificationIds(prev => {
                const isAlreadySelected = prev.some(id => id === selectedUserNotification.id);

                if (isAlreadySelected) return prev.filter(id => id !== selectedUserNotification.id);

                return [...prev, selectedUserNotification.id];
            });
        },
        [userNotifications?.userNotificationsList]
    );

    const tableColumns = useMemo(() => {
        const columns: TableColumn<UserNotificationView>[] = [
            {
                Header: () => (
                    <div className="table-header-dropdown-container f-r f-nw f-ai-c">
                        <Checkbox
                            isChecked={
                                userNotifications?.userNotificationsList.length === 0
                                    ? false
                                    : Boolean(
                                          userNotifications?.userNotificationsList.every(item =>
                                              selectedUserNotificationIds.some(i => i === item.id)
                                          )
                                      )
                            }
                            isPartial={Boolean(
                                userNotifications?.userNotificationsList.some(item =>
                                    selectedUserNotificationIds.some(i => i === item.id)
                                )
                            )}
                            onChange={addRemoveAllUserNotifications}
                            type="checkbox"
                            htmlFor="addRemoveAllUserNotifications"
                        />

                        {Boolean(selectedUserNotificationIds.length) && (
                            <div ref={actionDropdownRef}>
                                <Filter
                                    className="action-dropdown"
                                    isActive={false}
                                    showDropdown={showActionDropdown}
                                    onClick={handleActionDropdownClick}
                                >
                                    <div
                                        style={{ cursor: "pointer" }}
                                        onClick={() => setIsConfirmationModalOpen(true)}
                                    >
                                        OBRIŠI
                                        <Button isIcon size="medium" variant="warning-ghost">
                                            <Delete />
                                        </Button>
                                    </div>

                                    <div className="separator" />

                                    <div
                                        style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                                        onClick={() => {
                                            markNotificationsRead
                                                .mutateAsync(selectedUserNotificationIds)
                                                .then(_ => setSelectedUserNotificationIds([]));
                                        }}
                                    >
                                        OZNAČI KAO PROČITANO
                                        <Button isIcon size="medium" variant="secondary-ghost">
                                            <MailOpenIcon />
                                        </Button>
                                    </div>
                                </Filter>
                            </div>
                        )}
                    </div>
                ),
                accessor: "id",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: { row: Row<UserNotificationView> }) => (
                    <Checkbox
                        isChecked={selectedUserNotificationIds.some(id => id === row.original.id)}
                        onChange={() => addRemoveUserNotification(row.original.id)}
                        type="checkbox"
                        htmlFor={row.original.id}
                    />
                ),
            },
            {
                accessor: "isHighlighted",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: { row: Row<UserNotificationView> }) => {
                    const handleAction = () =>
                        highlightUserNotification.mutateAsync(row.original.id);

                    const commonProps = {
                        tabIndex: 0,
                        style: { cursor: "pointer" },
                        onClick: handleAction,
                        onKeyDown: (e: React.KeyboardEvent<SVGSVGElement>) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleAction();
                            }
                        },
                    };

                    return row.original.isHighlighted ? (
                        <StarFilledIcon {...commonProps} />
                    ) : (
                        <StarIcon {...commonProps} />
                    );
                },
            },
            {
                Header: "NAZIV",
                disableSortBy: true,
                width: 8,
                Cell: ({ row }: { row: Row<UserNotificationView> }) => {
                    const notificationUrl = `${AppRoutes.UserNotifications}?${RouteParams.NotificationId}=${row.original.id}`;

                    return (
                        <span
                            onClick={() => history.push(notificationUrl)}
                            style={{
                                cursor: "pointer",
                                fontWeight: row.original.readAt ? "normal" : "bold",
                            }}
                            tabIndex={0}
                            onKeyDown={e => handleKeyDown(e, () => history.push(notificationUrl))}
                        >
                            {row.original.subject}
                        </span>
                    );
                },
            },

            {
                Header: "IZVOR",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: { row: Row<UserNotificationView> }) => (
                    <span>
                        {getDropdownValue(row.original.source, NotificationSourceTranslation).label}
                    </span>
                ),
            },
            {
                Header: "DATUM",
                disableSortBy: true,
                width: 1,
                accessor: "scheduledAt",
                Cell: ({ row }: { row: Row<UserNotificationView> }) => (
                    <span>{getDisplayDateFormat(row.original.scheduledAt, false)}</span>
                ),
            },
        ];

        return columns;
    }, [selectedUserNotificationIds, userNotifications?.userNotificationsList, showActionDropdown]);

    return (
        <>
            <div className="user-notifications-container">
                <div className="user-notifications-content">
                    <div className="f-c g-24 f-1">
                        {selectedUserNotificationId && (
                            <UserNotificationDetails
                                userNotificationId={selectedUserNotificationId!}
                                onReturn={() => {
                                    history.push(AppRoutes.UserNotifications);
                                }}
                            />
                        )}

                        {!selectedUserNotificationId && (
                            <>
                                <UserNotificationsFilters
                                    ref={filtersRef}
                                    searchFilter={searchFilter}
                                    setSearchFilter={setSearchFilter}
                                    isHighlightedFilter={isHighlightedFilter}
                                    setIsHighlightedFilter={setIsHighlightedFilter}
                                    sourceFilter={sourceFilter}
                                    startDate={startDate}
                                    setStartDate={setStartDate}
                                    setEndDate={setEndDate}
                                    endDate={endDate}
                                    setSourceFilter={setSourceFilter}
                                    isReadFilter={isReadFilter}
                                    setIsReadFilter={setIsReadFilter}
                                    from={from}
                                    setFrom={setFrom}
                                    to={to}
                                    setTo={setTo}
                                    setCurrentPage={setCurrentPage}
                                />

                                <CustomTable
                                    data={userNotifications?.userNotificationsList ?? []}
                                    columns={tableColumns}
                                    flex
                                    loading={isLoading}
                                    tableType={TableType.UserNotificationsTable}
                                />

                                {!isLoading &&
                                    Boolean(userNotifications) &&
                                    userNotifications?.userNotificationsCount !== 0 && (
                                        <Pagination
                                            currentPage={currentPage}
                                            numberOfPages={Math.ceil(
                                                (userNotifications?.userNotificationsCount ?? 0) /
                                                    parseInt(pageSize.value)
                                            )}
                                            pageNeighbours={1}
                                            pageSize={pageSize}
                                            onPageSizeChange={v =>
                                                handlePageSizeChange(v, setPageSize, setCurrentPage)
                                            }
                                            handlePageClick={e =>
                                                handlePageClick(e, setCurrentPage)
                                            }
                                            handlePageSelect={e => setCurrentPage(e)}
                                            next={() => setCurrentPage(p => p + 1)}
                                            prev={() => setCurrentPage(p => p - 1)}
                                            first={() => setCurrentPage(1)}
                                            last={() =>
                                                setCurrentPage(
                                                    Math.ceil(
                                                        (userNotifications?.userNotificationsCount ??
                                                            0) / parseInt(pageSize.value)
                                                    )
                                                )
                                            }
                                        />
                                    )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmationModal
                content="Jeste li sigurni da želite obrisati odabrane poruke?"
                isActive={isConfirmationModalOpen}
                onConfirm={() => {
                    deleteUserNotifications.mutateAsync(selectedUserNotificationIds).then(_ => {
                        setSelectedUserNotificationIds([]);
                        setIsConfirmationModalOpen(false);
                    });
                }}
                onCancel={() => setIsConfirmationModalOpen(false)}
            />
        </>
    );
};
